/**
 * useUniverse — Custom Jardins d'Olé hook & provider
 * Manages active universe (jardinerie / paysagiste) with localStorage persistence.
 * Reads universe configurations from settings (universe_jardinerie_*, universe_paysagiste_*).
 * Applies CSS custom properties dynamically on universe switch.
 *
 * NON-CORE FILE — additive customization for the Jardins d'Olé client only.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useSettings } from "@/lib/useSettings";

const STORAGE_KEY = "jardins-ole.universe";
const DEFAULT_UNIVERSE = "jardinerie";
const VALID = ["jardinerie", "paysagiste"];

const UniverseCtx = createContext({
  universe: DEFAULT_UNIVERSE,
  setUniverse: () => {},
  config: null,
  enabled: false,
  configs: {},
});

export function UniverseProvider({ children }) {
  const { settings } = useSettings();
  const enabled = !!settings?.universes_enabled;

  const configs = useMemo(() => ({
    jardinerie: settings?.universe_jardinerie || null,
    paysagiste: settings?.universe_paysagiste || null,
  }), [settings]);

  const [universe, setUniverseState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VALID.includes(stored)) return stored;
    } catch { /* SSR/private */ }
    return DEFAULT_UNIVERSE;
  });

  // Re-sync default if settings change and the default differs
  useEffect(() => {
    if (!settings) return;
    const def = settings.universe_default;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored && def && VALID.includes(def)) {
        setUniverseState(def);
      }
    } catch { /* ignore */ }
  }, [settings]);

  const setUniverse = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setUniverseState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    // Trigger animation
    try {
      document.body.setAttribute("data-universe-switching", "yes");
      window.setTimeout(() => {
        document.body.removeAttribute("data-universe-switching");
      }, 950);
    } catch { /* ignore */ }
    // Notify listeners (e.g. analytics)
    try {
      window.dispatchEvent(new CustomEvent("jardins-ole:universe-changed", { detail: { universe: next } }));
    } catch { /* ignore */ }
  }, []);

  const config = configs[universe] || null;

  // Apply CSS custom properties + body data attribute on universe change
  useEffect(() => {
    if (!enabled || !config) return;
    const root = document.documentElement.style;
    if (config.primary_color) root.setProperty("--brand-navy", config.primary_color);
    if (config.secondary_color) root.setProperty("--brand-sky", config.secondary_color);
    if (config.accent_color) root.setProperty("--brand-red", config.accent_color);
    if (config.background_color) root.setProperty("--theme-bg", config.background_color);
    document.body.setAttribute("data-universe", universe);
    document.documentElement.setAttribute("data-universe", universe);
    // Update theme-color meta
    const tm = document.querySelector('meta[name="theme-color"]');
    if (tm && config.primary_color) tm.setAttribute("content", config.primary_color);
  }, [universe, config, enabled]);

  const value = useMemo(() => ({
    universe,
    setUniverse,
    config,
    configs,
    enabled,
  }), [universe, setUniverse, config, configs, enabled]);

  return <UniverseCtx.Provider value={value}>{children}</UniverseCtx.Provider>;
}

export const useUniverse = () => useContext(UniverseCtx);
